
/* istanbul ignore file */ // not fully implemented yet
import React, { useMemo, useContext, useCallback, useEffect } from 'react';
import _ from 'underscore';
import {
  bundleIcon,
  Wand24Regular,
  Wand24Filled,
  Glance24Regular,
  Glance24Filled,
} from '@fluentui/react-icons';

import { makeStyles, mergeClasses, tokens, DropdownProps, shorthands } from '@fluentui/react-components';

import { UiBlocker } from '@bingads-webui-fluent-component/ui-blocker';
import { withRecommendationContext } from '@bingads-webui-campaign-react/perf-max-recommendation';
import { withLoggingContext } from '@bingads-webui-campaign-react/copilot-loggers';
import { Scenario, Activity } from '@bingads-webui-react/hoc-with-signal';
import { Navigation } from './side-navigatioin';
import { AdjustView } from './adjust-view';
import { SizesView } from './sizes-view';
import { AdPreViews } from './ad-previews';
import { DisplayAdsContext, DisplayAdsProvider } from './context';
import { ActionTypes } from './reducer';
import { DisplayAdsEditorProps, AdSize } from './types';
import { DisplayTemplateSummaryBarControl } from './display-template-summary-bar-control';
import { getAdConfig, getAdConfigs } from './utils';

const NavigationIds = {
  Adjust: 'Adjust',
  Sizes: 'Sizes',
};

const useStyles = makeStyles({
  container: {
    display: 'flex',
    backgroundColor: tokens.colorNeutralBackground2Hover,
  },
  editPanel: {
    flexShrink: 0,
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalS),
    ...shorthands.overflow('hidden'),
  },
});

const Wand = bundleIcon(Wand24Filled, Wand24Regular);
const Glance = bundleIcon(Glance24Filled, Glance24Regular);

const groupedTabs = ({ onClick, i18n, selectedItem }) => [
  [
    {
      id: NavigationIds.Adjust,
      key: NavigationIds.Adjust,
      text: i18n.getString(_TL_('Adjust')),
      icon: Wand,
      isSelected: selectedItem === NavigationIds.Adjust,
      onClick: () => {
        onClick(NavigationIds.Adjust);
      },
    },
    {
      id: NavigationIds.Sizes,
      key: NavigationIds.Sizes,
      text: i18n.getString(_TL_('Sizes')),
      icon: Glance,
      isSelected: selectedItem === NavigationIds.Sizes,
      onClick: () => {
        onClick(NavigationIds.Sizes);
      },
    },
  ],
];

const cache = {};

const DisplayAdsEditorComponent: React.FC<DisplayAdsEditorProps> = ({
  i18n,
  showNavigation,
  displayTemplatesDataService,
  showRecommendationsBanner,
  hasUpdateBrandKitData,
  hasFetchBrandKitData,
  containerClassName,
  serverContext,
  brandKitDataService,
  brandkitApiState,
  preferenceService,
  instrumentation,
  perfMarker,
  aid,
  cid,
  oDataConfig,
  permissions,
  showSummaryBarControl,
  dataService,
  saveInNewFolder,
  onAdChange,
  setLoading,
  onSaveSuccess,
  sourceType,
  url,
  setDataLoaded,
  folderId,
}) => {
  const [selectedItem, setSelectedItem] = React.useState(NavigationIds.Adjust);
  const [isSinglePreviewSelected, setIsSinglePreviewSelected] = React.useState(false);
  const [zoomValue, setZoomValue] = React.useState('100%');
  const [loadingImage, setLoadingImage] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const hasFirstLoadWithAssets = React.useRef(!!url);
  const items = useMemo(
    () =>
      groupedTabs({
        onClick: setSelectedItem,
        selectedItem,
        i18n,
      }),
    [i18n, selectedItem]
  );
  const classes = useStyles();
  const {
    dispatch,
    state: {
      finalUrl,
      displayAds,
      templateConfigs,
      templateId2Size,
      adImages,
      isAssetsLoading,
      targetSizes,
      recommendedAssets,
    },
  } = useContext(DisplayAdsContext);
  const getBrandKits = brandKitDataService?.getBrandKits;
  const fetchNewAds = useMemo(() => {
    const { editDisplayAdsAssetRecommendations } = displayTemplatesDataService;
    return _.memoize(_.throttle(editDisplayAdsAssetRecommendations, 100), params => JSON.stringify(params));
  }, [displayTemplatesDataService]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const fetchNewDisplayAds = useCallback(() => {
    const ads = getAdConfigs(_.pick(displayAds, targetSizes), templateConfigs);
    const loadingAds = targetSizes.reduce((acc, size) => {
      acc[size] = {
        ...adImages[size],
        loading: true,
      };
      return acc;
    }, {});

    dispatch({ type: ActionTypes.updateAdImages, payload: loadingAds });

    const newAdImages = {};
    const adsToFetch = ads.filter(ad => !(JSON.stringify(ad) in cache));
    ads.forEach((ad) => {
      if (JSON.stringify(ad) in cache) {
        const sizeKey = templateId2Size[ad.template.templateId] as AdSize;
        newAdImages[sizeKey] = cache[JSON.stringify(ad)];
      }
    });
    dispatch({ type: ActionTypes.updateAdImages, payload: newAdImages });
    let hasCropSettingsUpdated = false;
    if (adsToFetch.length !== 0) {
      setLoadingImage(true);
      fetchNewAds(adsToFetch).then(({ Ads: generatedDisplayAds }) => {
        generatedDisplayAds.forEach((generatedAd) => {
          const { url: adUrl, template } = generatedAd;
          const sizeKey = templateId2Size[template.templateId] as AdSize;
          const config = templateConfigs[sizeKey];

          // update display ads
          if (displayAds?.[sizeKey]) {
            const imageAdsCropSettings = displayAds[sizeKey].image?.cropSetting;
            const logoAdsCropSettings = displayAds[sizeKey].logo?.cropSetting;
            const imageCropSettings = template?.image?.cropSetting;
            const logoCropSettings = template?.logo?.cropSetting;
            if (!imageAdsCropSettings && Array.isArray(imageCropSettings) && imageCropSettings.length > 0 && displayAds[sizeKey].image) {
              displayAds[sizeKey].image.cropSetting = imageCropSettings;
              hasCropSettingsUpdated = true;
            }
            if (!logoAdsCropSettings && Array.isArray(logoCropSettings) && logoCropSettings.length > 0 && displayAds[sizeKey].logo) {
              displayAds[sizeKey].logo.cropSetting = logoCropSettings;
              hasCropSettingsUpdated = true;
            }
          }

          if (config) {
            const { width, height } = config;
            newAdImages[sizeKey] = {
              url: adUrl,
              width,
              height,
              loading: false,
            };
            if (displayAds[sizeKey] !== undefined && templateConfigs[sizeKey] !== undefined) {
              const params = JSON.stringify(getAdConfig(displayAds[sizeKey], templateConfigs[sizeKey]));
              cache[params] = newAdImages[sizeKey];
            }
          }
        });
        if (hasCropSettingsUpdated) {
          dispatch({ type: ActionTypes.updateAds, payload: { adImages: newAdImages, displayAds } });
        } else {
          dispatch({ type: ActionTypes.updateAdImages, payload: newAdImages });
        }
      }).finally(() => {
        setLoadingImage(false);
      });
    }
  }, [
    dispatch,
    displayAds,
    targetSizes,
    templateConfigs,
    templateId2Size,
    adImages,
    fetchNewAds,
  ]);

  const fetchBrandKits = useCallback(async () => {
    if (getBrandKits) {
      perfMarker.willFetchData();
      const { response } = await getBrandKits(aid);
      perfMarker.willRender();
      if (setDataLoaded) {
        setDataLoaded();
      }
      if (Array.isArray(response) && response.length > 0) {
        const brandKit = response[0];
        const type = hasUpdateBrandKitData ? ActionTypes.updateBrandKit : ActionTypes.updateRecommendedAssetsWithBrandKit;
        dispatch({ type, payload: brandKit });
      }
    }
  }, [getBrandKits, aid, hasUpdateBrandKitData, dispatch, perfMarker, setDataLoaded]);

  useEffect(() => {
    fetchNewDisplayAds();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayAds]);

  useEffect(() => {
    if (onAdChange) {
      onAdChange({ displayAds, adImages, templateConfigs });
    }
  }, [displayAds, onAdChange, adImages, templateConfigs]);

  useEffect(() => {
    if (setLoading) {
      setLoading(loadingImage);
    }
  }, [loadingImage, setLoading]);

  const onBackgroundColorChange = useCallback((value) => {
    dispatch({ type: ActionTypes.updateBackground, payload: value });
  }, [dispatch]);
  const onImageChange = useCallback((value, keepCropSettings) => {
    dispatch({ type: ActionTypes.updateImage, payload: value, options: { keepCropSettings } });
  }, [dispatch]);
  const onLogoChange = useCallback((value) => {
    dispatch({ type: ActionTypes.updateLogo, payload: value });
  }, [dispatch]);
  const onLongHeadlineChange = useCallback((value) => {
    dispatch({ type: ActionTypes.updateLongHeadLine, payload: { text: value } });
  }, [dispatch]);
  const onShortHeadlineChange = useCallback((value) => {
    dispatch({ type: ActionTypes.updateShortHeadLine, payload: { text: value } });
  }, [dispatch]);
  const onDescriptionChange = useCallback((value) => {
    dispatch({ type: ActionTypes.updateDescription, payload: { text: value } });
  }, [dispatch]);
  const onCtaChange = useCallback((value) => {
    dispatch({ type: ActionTypes.updateCallToAction, payload: { text: value } });
  }, [dispatch]);
  const onAssetPropertiesWithColorChange = useCallback(
    (payload) => {
      dispatch({
        type: ActionTypes.updateAssetPropertyAndColor,
        payload,
      });
    },
    [dispatch]
  );

  const updateTextAssets = useCallback((payload) => {
    const { description, shortHeadline, longHeadline } = payload;
    if (description !== undefined) {
      onDescriptionChange(description);
    }
    if (shortHeadline !== undefined) {
      onShortHeadlineChange(shortHeadline);
    }
    if (longHeadline !== undefined) {
      onLongHeadlineChange(longHeadline);
    }
  }, [onDescriptionChange, onLongHeadlineChange, onShortHeadlineChange]);

  useEffect(() => {
    hasFetchBrandKitData && fetchBrandKits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFetchBrandKitData]);
  const filterSelectionCallback = useCallback((selectedFilter) => {
    setZoomValue('100%');
    setIsSinglePreviewSelected(false);
    dispatch({ type: ActionTypes.updateTargetSizes, payload: selectedFilter });
  }, [dispatch]);

  const editIndividualAdCallback = useCallback((selectedSize) => {
    setIsSinglePreviewSelected(true);
    dispatch({ type: ActionTypes.updateTargetSizes, payload: selectedSize });
  }, [dispatch]);
  const onRecommendedAssetsChange = useCallback((value) => {
    dispatch({ type: ActionTypes.updateRecommendedAssets, payload: value, options: { hasUpdateTemplate: hasFirstLoadWithAssets.current } });
    hasFirstLoadWithAssets.current = false;
  }, [dispatch]);
  const onFinalUrlChange = useCallback((value) => {
    dispatch({ type: ActionTypes.updateFinalUrl, payload: value });
  }, [dispatch]);

  const onZoomSelect: DropdownProps['onOptionSelect'] = (event, data) => {
    setZoomValue(data.selectedOptions[0]);
  };
  const isBrandKitLoading = brandkitApiState?.brandkitLoading;
  return (
    <>
      {showSummaryBarControl && (
        <DisplayTemplateSummaryBarControl
          i18n={i18n}
          onZoomSelect={onZoomSelect}
          zoomValue={zoomValue}
          isSaveDisabled={loadingImage || isSaving}
          dataService={dataService}
          saveInNewFolder={saveInNewFolder}
          onSaveSuccess={onSaveSuccess}
          setIsSaving={setIsSaving}
          finalUrl={finalUrl}
          sourceType={sourceType}
          folderId={folderId}
        />
      )}
      <UiBlocker
        blocking={isSaving || isBrandKitLoading}
        loadingText={isSaving? i18n.getString(_TL_('Saving...')): i18n.getString(_TL_('loading...'))}
      >
        <div
          className={mergeClasses(
            'display-ads-editor',
            classes.container,
            containerClassName
          )}
        >
          {showNavigation && <Navigation items={items} />}
          <div className={mergeClasses(classes.editPanel)}>
            {selectedItem === NavigationIds.Adjust && (
              <AdjustView
                showRecommendationsBanner={showRecommendationsBanner}
                displayAds={displayAds}
                targetSizes={targetSizes}
                templateConfigs={templateConfigs}
                finalUrl={finalUrl}
                instrumentation={instrumentation}
                i18n={i18n}
                onFinalUrlChange={onFinalUrlChange}
                onRecommendedAssetsChange={onRecommendedAssetsChange}
                onBackgroundColorChange={onBackgroundColorChange}
                onImageChange={onImageChange}
                onLogoChange={onLogoChange}
                onChangeTextAssets={updateTextAssets}
                onAssetPropertiesWithColorChange={
                  onAssetPropertiesWithColorChange
                }
                onCtaChange={onCtaChange}
                permissions={permissions}
                aid={aid}
                recommendedAssets={recommendedAssets}
                cid={cid}
                perfMarker={perfMarker}
                preferenceService={preferenceService}
                oDataConfig={oDataConfig}
                serverContext={serverContext}
                isAssetsLoading={isAssetsLoading}
              />
            )}
            {selectedItem === NavigationIds.Sizes && <SizesView />}
          </div>
          <AdPreViews
            i18n={i18n}
            adImages={adImages}
            targetSizes={targetSizes}
            isSinglePreviewSelected={isSinglePreviewSelected}
            editIndividualAdCallback={editIndividualAdCallback}
            filterSelectionCallback={filterSelectionCallback}
            zoomValue={zoomValue}
            onZoomSelect={onZoomSelect}
            showPreviewControls={!showSummaryBarControl}
          />
        </div>
      </UiBlocker>
    </>
  );
};

const DisplayAdsEditorWithContext = (props) => {
  const {
    templateData,
    scenarioName,
    activityName,
    url,
    ...remainingProps
  } = props;

  return (
    <Scenario name={scenarioName || 'DisplayTemplateEditor'}>
      <Activity name={activityName ||'DisplayTemplateEditor'}>
        <DisplayAdsProvider templateData={templateData} finalUrl={url}>
          <DisplayAdsEditorComponent url={url} {...remainingProps} />
        </DisplayAdsProvider>
      </Activity>
    </Scenario>
  );
};


export const DisplayAdsEditor = withLoggingContext(withRecommendationContext(DisplayAdsEditorWithContext));

